import React, { useState } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";
import AddIcon from "@mui/icons-material/Add";
import SwitchToggle from "../../components/SwitchToggle";
import AddFeature from "../../Modals/addFeature";
import AddFeatureSubGroup from "../../Modals/addFeatureSubGroup";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import {
  useGetFeatureGroupDetailsQuery,
  useUpdateFeatureGroupMutation,
  useDeleteFeatureGroupMutation
} from "../../service/features/api";
import { Group_Type, Field_Type } from "../../constants/enum";
import moment from "moment";
import { toast } from "react-toastify";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";

const FeatureGroupDetails: React.FC = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");

  const [openSubGroupModal, setOpenSubGroupModal] = useState(false);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedSubFeature, setSelectedSubFeature] = useState<any>(null);

  const { data: featureGroupData, isLoading, isError } = useGetFeatureGroupDetailsQuery(groupId);
  const [updateFeatureGroup] = useUpdateFeatureGroupMutation();
  const [deleteFeatureGroup, { isLoading: isDeleting }] = useDeleteFeatureGroupMutation();

  const groupData = featureGroupData?.data || {};
  const subFeatures = groupData.options || [];

  const getGroupTypeName = (value?: number) => {
    if (!value) return "-";
    const entry = Object.entries(Group_Type).find(([_, val]) => val === value);
    if (!entry) return "-";
    return entry[0].split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const getFieldTypeName = (value?: number) => {
    if (!value) return "-";
    const entry = Object.entries(Field_Type).find(([_, val]) => val === value);
    if (!entry) return "-";
    return entry[0].charAt(0).toUpperCase() + entry[0].slice(1).toLowerCase();
  };

  const handleToggle = async (subFeature: any, checked: boolean) => {
    try {
      const res = await updateFeatureGroup({
        id: subFeature._id,
        isActive: checked,
        name: subFeature.name,
        groupType: subFeature.groupType || groupData.groupType,
        fieldType: subFeature.fieldType || groupData.fieldType,
        type: subFeature.type || groupData.type || 1,
        parentFeatureId: groupId,
        categoryId: subFeature.categoryId || groupData.categoryId || [],
        order: subFeature.order || 1
      }).unwrap();
      toast.success(res?.message || "Status updated successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update status");
    }
  };

  const handleDeleteClick = (subFeature: any) => {
    setSelectedSubFeature(subFeature);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSubFeature?._id) return;
    try {
      const res = await deleteFeatureGroup(selectedSubFeature._id).unwrap();
      toast.success(res?.message || "Sub-feature deleted successfully");
      setOpenDeleteModal(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete sub-feature");
    }
  };

  const handleEdit = (subFeature: any) => {
    setSelectedSubFeature(subFeature);
    setOpenSubGroupModal(true);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h2>Failed to load feature group details</h2>
        <Button onClick={() => navigate("/manage-features")}>Go Back</Button>
      </div>
    );
  }

  return (
    <>
      <CommonBreadcrumb
        title={`${groupData.name || "Feature Group"} : Feature Group Details`}
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Manage Features", onClick: () => navigate("/manage-features") },
          { label: "Group Detail" },
        ]}
      />


      {/* ================= Sub-Features ================= */}
      <div className="cards mt_20">
        <div className="table_header">
          <h3>Sub-Features</h3>
          <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
            <Button
              className="btn btn_primary"
              onClick={() => {
                setSelectedSubFeature(null);
                setOpenSubGroupModal(true);
              }}
            >
              <AddIcon /> Add Sub-Feature
            </Button>
          </Can>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sub-Feature Name</TableCell>
                <TableCell>Group Type</TableCell>
                <TableCell>Field Type</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Disable/Enable</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {subFeatures.map((sf: any) => (
                <TableRow key={sf._id}>
                  <TableCell>
                    <div className="user_block">
                      <p><b>{sf.name}</b></p>
                    </div>
                  </TableCell>

                  <TableCell>{getGroupTypeName(sf.groupType || groupData.groupType)}</TableCell>

                  <TableCell>{getFieldTypeName(sf.fieldType || groupData.fieldType)}</TableCell>

                  <TableCell>{moment(sf.createdAt).format("DD-MMM-YYYY")}</TableCell>

                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={sf.isActive}
                        onChange={(e) => handleToggle(sf, e.target.checked)}
                      />
                    ) : (
                      <span className={`status_btn ${sf.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                        {sf.isActive ? "Enabled" : "Disabled"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell>
                    <div className="table_actions v2">
                      <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                        <Tooltip title="Edit" arrow>
                          <IconButton onClick={() => handleEdit(sf)}>
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton>
                        </Tooltip>
                      </Can>
                      <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                        <Tooltip title="Delete" arrow>
                          <IconButton onClick={() => handleDeleteClick(sf)}>
                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                          </IconButton>
                        </Tooltip>
                      </Can>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {subFeatures.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">No Sub-Features Found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <AddFeatureSubGroup
        open={openSubGroupModal}
        setOpen={setOpenSubGroupModal}
        groupId={groupId}
        featureData={selectedSubFeature}
      />

      <AddFeature
        open={openFeatureModal}
        setOpen={setOpenFeatureModal}
        groupId={Number(groupId)}
        subGroups={[]} // This might need update later if we support nested features
      />

      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Sub-Feature"
        message={`Are you sure you want to delete "${selectedSubFeature?.name}"?`}
      />
    </>
  );
};

export default FeatureGroupDetails;
